import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import RoutesPath from "@/constants/RoutesPath";

const MAX_REFRESH_ATTEMPTS = 4;
const REFRESH_COUNT_KEY = "refresh_attempt_count";

const getRefreshCount = (): number => {
  const count = sessionStorage.getItem(REFRESH_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
};

const setRefreshCount = (count: number): void => {
  sessionStorage.setItem(REFRESH_COUNT_KEY, count.toString());
};

const clearRefreshCount = (): void => {
  sessionStorage.removeItem(REFRESH_COUNT_KEY);
};

interface RefreshOptions {
  interval?: number; // in milliseconds
  onRefresh: () => void;
}

const useRefresh = ({
  interval = 15000, // default 15 seconds
  onRefresh,
}: RefreshOptions) => {
  const navigate = useNavigate();
  const { token } = useParams();
  const onRefreshRef = useRef(onRefresh);
  const navigateRef = useRef(navigate);
  const tokenRef = useRef(token);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
    navigateRef.current = navigate;
    tokenRef.current = token;
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentCount = getRefreshCount() + 1;
      setRefreshCount(currentCount);
      console.log("Refresh attempt:", currentCount);

      if (currentCount >= MAX_REFRESH_ATTEMPTS) {
        clearInterval(intervalId);
        clearRefreshCount();
        void navigateRef.current(
          `/${tokenRef.current}${RoutesPath.PRE_AUTHORIZATION_ERROR}`,
        );
        return;
      }

      onRefreshRef.current();
    }, interval);

    return () => clearInterval(intervalId);
  }, [interval]);
};

export default useRefresh;
